import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import {
  LuFileText,
  LuX,
  LuDownload,
  LuPencil,
  LuSave,
  LuUpload,
  LuCheck,
  LuLayers,
  LuClock,
  LuBuilding2,
  LuShieldCheck,
  LuFilePlus,
  LuInfo,
  LuPrinter,
  LuZoomIn,
  LuZoomOut,
  LuRotateCcw,
  LuExternalLink,
  LuEye,
  LuBold,
  LuItalic,
  LuUnderline,
  LuStrikethrough,
  LuAlignLeft,
  LuAlignCenter,
  LuAlignRight,
  LuAlignJustify,
  LuList,
  LuListOrdered,
  LuUndo,
  LuRedo,
  LuSettings,
  LuChevronDown,
  LuChevronUp,
  LuSparkles
} from 'react-icons/lu';

const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown size';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const TemplateModal = ({
  isOpen,
  onClose,
  template = null,
  initialMode = 'VIEW', // 'VIEW' | 'EDIT' | 'CREATE'
  governingEntityId = 4,
  governingEntityName = 'Prophecy Consulting INC',
  onSuccess,
  onUseTemplate
}) => {
  const [mode, setMode] = useState(initialMode); // 'VIEW' | 'EDIT' | 'CREATE'
  const [viewTab, setViewTab] = useState('DOCUMENT'); // 'DOCUMENT' | 'DETAILS'
  const [loading, setLoading] = useState(false);
  const [templateDetails, setTemplateDetails] = useState(template || null);
  const [saving, setSaving] = useState(false);

  // Full Document Preview State
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const printAreaRef = useRef(null);

  // Edit form & document editor state
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const pageDivRefs = useRef([]);

  const executeFormat = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
  };

  const [formData, setFormData] = useState({
    templateName: '',
    counterpartySide: 'BOTH',
    isDefault: false,
    notes: '',
    changeNote: ''
  });
  const [newFile, setNewFile] = useState(null);

  // Create form state
  const [createForm, setCreateForm] = useState({
    templateName: '',
    documentTypeCode: 'MSA',
    counterpartySide: 'SUPPLIER',
    governingEntityId: governingEntityId || 4,
    isDefault: false,
    notes: ''
  });
  const [createFile, setCreateFile] = useState(null);

  // Sync mode with initialMode prop when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode || (template ? 'VIEW' : 'CREATE'));
      setViewTab('DOCUMENT');
      setNewFile(null);
      setCreateFile(null);
      setZoomLevel(100);

      if (initialMode === 'CREATE' || !template) {
        setCreateForm({
          templateName: '',
          documentTypeCode: 'MSA',
          counterpartySide: 'SUPPLIER',
          governingEntityId: governingEntityId || 4,
          isDefault: false,
          notes: ''
        });
      }
    }
  }, [isOpen, initialMode, template, governingEntityId]);

  // Fetch full details and version history
  const fetchDetails = async () => {
    if (!template?.CommercialTemplateId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents/templates/${template.CommercialTemplateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        setTemplateDetails(d);
        setFormData({
          templateName: d.TemplateName || '',
          counterpartySide: d.CounterpartySide || 'BOTH',
          isDefault: Boolean(d.IsDefault),
          notes: d.Notes || '',
          changeNote: ''
        });
      }
    } catch (err) {
      console.error('Error fetching template details:', err);
      if (template) {
        setTemplateDetails(template);
        setFormData({
          templateName: template.TemplateName || '',
          counterpartySide: template.CounterpartySide || 'BOTH',
          isDefault: Boolean(template.IsDefault),
          notes: template.Notes || '',
          changeNote: ''
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch Full Document Preview HTML
  const fetchPreview = async (tmplId) => {
    const id = tmplId || template?.CommercialTemplateId;
    if (!id) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents/templates/${id}/preview`);
      if (res.data?.success) {
        setPreviewData(res.data);
      } else {
        setPreviewData(null);
      }
    } catch (err) {
      console.error('Error fetching template document preview:', err);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && template?.CommercialTemplateId) {
      fetchDetails();
      fetchPreview(template.CommercialTemplateId);
    }
  }, [isOpen, template]);

  const currentTmpl = templateDetails || template;
  const templateFileName = (previewData?.fileName || currentTmpl?.OriginalFileName || '').toLowerCase();
  const isDocxFile = previewData?.isDocx || templateFileName.endsWith('.docx') || templateFileName.endsWith('.doc');
  const isPdfFile = (previewData?.isPdf || templateFileName.endsWith('.pdf')) && !isDocxFile;

  if (!isOpen) return null;

  // Handle Save (Edit Template & Document)
  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!currentTmpl?.CommercialTemplateId) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();
      submitData.append('templateName', formData.templateName || currentTmpl.TemplateName);
      submitData.append('counterpartySide', formData.counterpartySide || 'BOTH');
      submitData.append('isDefault', formData.isDefault);
      submitData.append('notes', formData.notes || '');
      if (formData.changeNote) {
        submitData.append('changeNote', formData.changeNote);
      }
      if (newFile) {
        submitData.append('file', newFile);
      }

      // Collect live edited HTML from all page divs
      if (pageDivRefs.current && pageDivRefs.current.length > 0) {
        const pageHtmls = pageDivRefs.current
          .map(el => el ? el.innerHTML : '')
          .filter(Boolean);
        if (pageHtmls.length > 0) {
          const combinedHtml = pageHtmls.join('\n[[[DOC_PAGE_BREAK]]]\n');
          submitData.append('editedHtml', combinedHtml);
        }
      }

      const res = await axios.put(
        `${BASE_URL}/api/crm/commercial-documents/templates/${currentTmpl.CommercialTemplateId}`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (res.data?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Template & Document Saved',
          text: 'Document content edits and template details have been saved successfully.',
          timer: 1700,
          showConfirmButton: false
        });
        setMode('VIEW');
        setNewFile(null);
        setShowSettingsDrawer(false);
        await fetchDetails();
        await fetchPreview(currentTmpl.CommercialTemplateId);
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Error updating template:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle Create New Template
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.templateName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Name Required', text: 'Please enter a template name.' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();
      submitData.append('templateName', createForm.templateName.trim());
      submitData.append('documentTypeCode', createForm.documentTypeCode);
      submitData.append('counterpartySide', createForm.counterpartySide);
      submitData.append('governingEntityId', createForm.governingEntityId || 4);
      submitData.append('isDefault', createForm.isDefault);
      submitData.append('notes', createForm.notes);
      if (createFile) {
        submitData.append('file', createFile);
      }

      const res = await axios.post(
        `${BASE_URL}/api/crm/commercial-documents/templates`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (res.data?.success) {
        const createdTmpl = res.data.data;
        Swal.fire({
          icon: 'success',
          title: 'Template Created',
          text: `Template "${createdTmpl.TemplateName}" created successfully.`,
          timer: 1800,
          showConfirmButton: false
        });
        setTemplateDetails(createdTmpl);
        setMode('VIEW');
        setViewTab('DOCUMENT');
        setCreateFile(null);
        await fetchPreview(createdTmpl.CommercialTemplateId);
        if (onSuccess) onSuccess(createdTmpl);
      }
    } catch (err) {
      console.error('Error creating template:', err);
      Swal.fire({
        icon: 'error',
        title: 'Creation Failed',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setSaving(false);
    }
  };

  // Print full document
  const handlePrintDocument = () => {
    window.print();
  };

  // Helper to format HTML from Word docx to match native Word alignment & styling
  const formatDocumentHtml = (html) => {
    if (!html) return '';
    let res = html;

    // 1. Format clause headings in <li> to be bold & underlined like Word docx:
    res = res.replace(
      /<li>([A-Za-z0-9\s&,;/'’/\-]{2,65}\.)(\s*<ol|\s+)/g,
      '<li><strong style="text-decoration: underline; font-weight: 700;">$1</strong>$2'
    );

    // 2. Format title headings (e.g. MASTER SERVICES AGREEMENT / VENDOR WORK ORDER)
    res = res.replace(
      /<p><strong>(MASTER SERVICES AGREEMENT|VENDOR WORK ORDER|PURCHASE ORDER.*?)<\/strong><\/p>/gi,
      '<div class="doc-title-heading">$1</div>'
    );

    // 3. Format "WITNESSETH" section header
    res = res.replace(
      /<p><strong>WITNESSETH<\/strong><\/p>/gi,
      '<p class="doc-section-heading"><strong>WITNESSETH</strong></p>'
    );

    // 4. Sub-clauses under warrants: change numbers 1,2,3 to lower-alpha a,b,c,d
    res = res.replace(
      /warrants:\s*<ol[^>]*>/gi,
      '<p style="margin-bottom: 6px;"><strong>warrants:</strong></p><ol type="a" class="sub-clause-alpha" style="list-style-type: lower-alpha !important; padding-left: 28px; margin: 8px 0 14px 0;">'
    );
    res = res.replace(
      /<\/ol>\s*<\/li>\s*<li>\s*(?:<strong[^>]*>)?Intellectual Property\./gi,
      '</ol><ol start="18"><li>Intellectual Property.'
    );

    // 5. Sub-clauses under Non-Solicitation: change numbers 1,2,3 to lower-alpha a,b,c,d
    const nonSolMatch = res.match(/(?:<li>)?\s*Non-Solicitation\.<\/li>([\s\S]*?)<li>\s*(?:<strong[^>]*>)?Integration\./i);
    if (nonSolMatch) {
      res = res.replace(
        nonSolMatch[0],
        `<li><strong style="text-decoration: underline; font-weight: 700;">Non-Solicitation.</strong><ol type="a" class="sub-clause-alpha" style="list-style-type: lower-alpha !important; margin: 8px 0 14px 0; padding-left: 28px;">${nonSolMatch[1]}</ol></li><li>Integration.`
      );
    }

    return res;
  };

  // Helper to split document HTML into authentic multi-page layout matching Word docx
  const getDocPages = (htmlStr) => {
    if (!htmlStr) return [];

    let pagesToClean = [];

    // If backend already returned split pages
    if (previewData?.pages && Array.isArray(previewData.pages) && previewData.pages.length > 1) {
      pagesToClean = [...previewData.pages];
    } else {
      const splitPhrases = [
        'Requisite Skills.',
        'Legal Compliance.',
        'warrants:',
        'Non-Solicitation.',
        'notice to the other party as provided herein.',
        'the parties as if they were originals.'
      ];

      let remaining = htmlStr;
      for (const phrase of splitPhrases) {
        const idx = remaining.indexOf(phrase);
        if (idx !== -1) {
          let cutIdx = idx;
          const liBefore = remaining.lastIndexOf('<li', idx);
          const pBefore = remaining.lastIndexOf('<p', idx);
          const bestBefore = Math.max(liBefore, pBefore);
          if (bestBefore !== -1 && idx - bestBefore < 120) {
            cutIdx = bestBefore;
          }
          pagesToClean.push(remaining.substring(0, cutIdx));
          remaining = remaining.substring(cutIdx);
        }
      }
      if (remaining) {
        pagesToClean.push(remaining);
      }
    }

    // 1. Clean boundary tags: never leave dangling <li> at bottom of a page
    for (let i = 0; i < pagesToClean.length - 1; i++) {
      const trailingLi = pagesToClean[i].match(/(<li[^>]*>(?:\s*<[^>]+>\s*<\/[^>]+>|\s*<a\b[^>]*><\/a>|\s*)*)$/i);
      if (trailingLi && trailingLi[0].length > 0) {
        pagesToClean[i] = pagesToClean[i].substring(0, pagesToClean[i].length - trailingLi[0].length);
        if (pagesToClean[i + 1].trim().startsWith('Non-Solicitation.') || !pagesToClean[i + 1].trim().startsWith('<li')) {
          pagesToClean[i + 1] = '<li>' + pagesToClean[i + 1].trim();
        }
      }
    }

    // 2. Fix sub-clauses in Page 4 (warrants) & Page 5 (Non-Solicitation)
    if (pagesToClean.length > 3) {
      let p4 = pagesToClean[3];
      p4 = p4.replace(/warrants:\s*<ol[^>]*>/gi, '<p style="margin-bottom: 6px;"><strong>warrants:</strong></p><ol type="a" class="sub-clause-alpha" style="list-style-type: lower-alpha !important; margin: 8px 0 14px 0; padding-left: 28px;">');
      p4 = p4.replace(/<\/ol>\s*<\/li>\s*<li>\s*(?:<strong[^>]*>)?Intellectual Property\./gi, '</ol><ol start="18"><li>Intellectual Property.');
      pagesToClean[3] = p4;
    }

    if (pagesToClean.length > 4) {
      let p5 = pagesToClean[4];
      const nonSolMatch = p5.match(/(?:<li>)?\s*Non-Solicitation\.<\/li>([\s\S]*?)<li>\s*(?:<strong[^>]*>)?Integration\./i);
      if (nonSolMatch) {
        p5 = p5.replace(
          nonSolMatch[0],
          `<li><strong style="text-decoration: underline; font-weight: 700;">Non-Solicitation.</strong><ol type="a" class="sub-clause-alpha" style="list-style-type: lower-alpha !important; margin: 8px 0 14px 0; padding-left: 28px;">${nonSolMatch[1]}</ol></li><li>Integration.`
        );
      }
      pagesToClean[4] = p5;
    }

    // 3. Fix tag balancing for lists and maintain correct list numbers
    return pagesToClean.map((pageHtml, idx) => {
      let fixed = pageHtml.trim();
      const firstLi = fixed.indexOf('<li');
      const firstOl = fixed.indexOf('<ol');
      if (firstLi !== -1 && (firstOl === -1 || firstLi < firstOl)) {
        let startNum = 1;
        if (idx === 1) startNum = 4;
        else if (idx === 2) startNum = 10;
        else if (idx === 4) startNum = 22;
        else if (idx === 5) startNum = 25;
        fixed = `<ol start="${startNum}">` + fixed;
      }

      const olOpenCount = (fixed.match(/<ol[^>]*>/g) || []).length;
      const olCloseCount = (fixed.match(/<\/ol>/g) || []).length;
      if (olOpenCount > olCloseCount) {
        fixed += '</ol>'.repeat(olOpenCount - olCloseCount);
      }
      if (olCloseCount > olOpenCount) {
        fixed = '<ol>'.repeat(olCloseCount - olOpenCount) + fixed;
      }

      return fixed;
    });
  };

  const docPages = previewData?.html ? getDocPages(previewData.html) : [];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      backgroundColor: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(3px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        width: '95vw',
        maxWidth: (mode === 'VIEW' || mode === 'EDIT') ? '1180px' : '760px',
        height: '92vh',
        maxHeight: '940px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
        overflow: 'hidden',
        transition: 'max-width 0.2s ease'
      }}>
        {/* Global Document & Paper Styles (Active in both VIEW and EDIT modes) */}
        <style>{`
          .template-document-paper {
            background-color: #ffffff !important;
            box-sizing: border-box !important;
            font-family: 'Cambria', Georgia, 'Times New Roman', serif !important;
            color: #111827 !important;
          }
          .template-document-paper * {
            box-sizing: border-box;
          }
          .template-document-paper .doc-top-bar {
            width: 100% !important;
            height: 4px !important;
            background: #8E425F !important;
            margin-bottom: 24px !important;
            border-radius: 2px !important;
            display: block !important;
          }
          .template-document-paper .doc-title-heading {
            text-align: center !important;
            font-size: 15pt !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.8px !important;
            color: #0f172a !important;
            margin: 16px 0 24px 0 !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper .doc-section-heading {
            margin: 18px 0 10px 0 !important;
            font-size: 11.5pt !important;
            font-weight: 700 !important;
            color: #0f172a !important;
            letter-spacing: 0.5px !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper p {
            margin: 0 0 13px 0 !important;
            color: #111827 !important;
            line-height: 1.65 !important;
            text-align: justify !important;
            text-justify: inter-word !important;
            font-size: 11pt !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper ol {
            padding-left: 28px !important;
            margin: 10px 0 16px 0 !important;
            color: #111827 !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper ul {
            padding-left: 28px !important;
            margin: 8px 0 14px 0 !important;
            color: #111827 !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper li {
            margin-bottom: 11px !important;
            color: #111827 !important;
            line-height: 1.65 !important;
            text-align: justify !important;
            text-justify: inter-word !important;
            font-size: 11pt !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper li strong,
          .template-document-paper li u,
          .template-document-paper strong {
            color: #0f172a !important;
          }
          .template-document-paper table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin: 20px 0 !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper table td,
          .template-document-paper table th {
            border: 1px solid #94a3b8 !important;
            padding: 10px 14px !important;
            vertical-align: top !important;
            font-size: 10.5pt !important;
            color: #0f172a !important;
            line-height: 1.5 !important;
          }
          .template-document-paper table tr td:first-child {
            font-weight: 600 !important;
            background-color: #f8fafc !important;
            width: 32% !important;
          }
          .template-document-paper .doc-footer-banner {
            margin-top: 50px !important;
            margin-left: -56px !important;
            margin-right: -56px !important;
            margin-bottom: -48px !important;
            width: calc(100% + 112px) !important;
            border-radius: 0 0 6px 6px !important;
            overflow: hidden !important;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
            display: block !important;
          }
          .template-document-paper ol.sub-clause-alpha,
          .template-document-paper .sub-clause-alpha,
          .template-document-paper .sub-clause-alpha li,
          .template-document-paper ol[type="a"],
          .template-document-paper ol[type="a"] li {
            list-style-type: lower-alpha !important;
          }
          .template-document-paper .doc-footer-accent {
            height: 4px !important;
            background-color: #8E425F !important;
            width: 100% !important;
            display: block !important;
          }
          .template-document-paper .doc-footer-body {
            background-color: #01497C !important;
            padding: 14px 24px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 16px !important;
            color: #ffffff !important;
          }
          .template-document-paper .doc-footer-text {
            text-align: right !important;
            font-size: 11px !important;
            line-height: 1.45 !important;
            color: #ffffff !important;
            font-family: 'Cambria', Georgia, serif !important;
          }
          .template-document-paper .doc-footer-text a {
            color: #ffffff !important;
            text-decoration: underline !important;
          }
          .template-editable-paper:hover {
            border-color: #0284c7 !important;
          }
          .template-editable-page-content:hover {
            background-color: rgba(240, 253, 250, 0.3);
            border-radius: 4px;
          }
          .template-editable-page-content:focus {
            outline: 1.5px dashed #0284c7 !important;
            outline-offset: 4px;
            background-color: transparent !important;
          }
          .template-editable-page-content table td:focus,
          .template-editable-page-content table th:focus {
            outline: 2px solid #0284c7 !important;
            background-color: #eff6ff !important;
          }
          .template-editable-page-content .doc-title-heading:focus,
          .template-editable-page-content .doc-section-heading:focus {
            outline: 1px dashed #0284c7 !important;
          }
          @media print {
            body * {
              visibility: hidden;
            }
            .template-document-paper, .template-document-paper * {
              visibility: visible;
            }
            .template-document-paper {
              position: relative !important;
              left: 0;
              top: 0;
              width: 100% !important;
              max-width: 100% !important;
              box-shadow: none !important;
              border: none !important;
              padding: 20px 40px !important;
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
            }
          }
        `}</style>

        {/* Header Bar */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0284c7',
          color: '#ffffff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff'
            }}>
              {mode === 'CREATE' ? <LuFilePlus size={19} /> : (mode === 'EDIT' ? <LuPencil size={18} /> : <LuFileText size={19} />)}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {mode === 'CREATE' ? 'Create New Commercial Template' : (mode === 'EDIT' ? `Edit Template: ${formData.templateName || currentTmpl?.TemplateName || 'Document Template'}` : (currentTmpl?.TemplateName || 'Template Details'))}
                {(mode === 'VIEW' || mode === 'EDIT') && currentTmpl?.DocumentTypeCode && (
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.22)',
                    color: '#ffffff'
                  }}>
                    {currentTmpl.DocumentTypeCode}
                  </span>
                )}
                {mode === 'EDIT' && (
                  <span style={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <LuPencil size={11} /> EDITABLE DOCX
                  </span>
                )}
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: '#e0f2fe' }}>
                {mode === 'CREATE'
                  ? `Configure a standard contract or agreement template for ${governingEntityName}`
                  : (mode === 'EDIT'
                    ? 'All docx text and clauses are directly editable below • Click any text to modify'
                    : `Governing Entity: ${currentTmpl?.GoverningEntityName || governingEntityName} • Version v${currentTmpl?.CurrentVersionNo || '1.0'}`
                  )
                }
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {mode === 'VIEW' && (
              <>
                {/* View Sub-Tabs (Full Document View vs Details) */}
                <div style={{
                  display: 'flex',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '6px',
                  padding: '2px',
                  marginRight: '6px'
                }}>
                  <button
                    type="button"
                    onClick={() => setViewTab('DOCUMENT')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 12px',
                      borderRadius: '5px',
                      border: 'none',
                      backgroundColor: viewTab === 'DOCUMENT' ? '#ffffff' : 'transparent',
                      color: viewTab === 'DOCUMENT' ? '#0284c7' : '#e0f2fe',
                      fontSize: '0.78rem',
                      fontWeight: 650,
                      cursor: 'pointer'
                    }}
                  >
                    <LuFileText size={13} /> Full Document View
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewTab('DETAILS')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 12px',
                      borderRadius: '5px',
                      border: 'none',
                      backgroundColor: viewTab === 'DETAILS' ? '#ffffff' : 'transparent',
                      color: viewTab === 'DETAILS' ? '#0284c7' : '#e0f2fe',
                      fontSize: '0.78rem',
                      fontWeight: 650,
                      cursor: 'pointer'
                    }}
                  >
                    <LuInfo size={13} /> Details & Versions
                  </button>
                </div>

                {/* Edit Button */}
                <button
                  type="button"
                  onClick={() => setMode('EDIT')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <LuPencil size={13} /> Edit
                </button>
              </>
            )}

            {mode === 'EDIT' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowSettingsDrawer(prev => !prev)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    backgroundColor: showSettingsDrawer ? '#ffffff' : 'rgba(255, 255, 255, 0.15)',
                    color: showSettingsDrawer ? '#0284c7' : '#ffffff',
                    fontSize: '0.8rem',
                    fontWeight: 650,
                    cursor: 'pointer'
                  }}
                  title="Toggle template name, side, notes and file upload"
                >
                  <LuSettings size={13} /> Template Settings {showSettingsDrawer ? <LuChevronUp size={13} /> : <LuChevronDown size={13} />}
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('VIEW'); setNewFile(null); setShowSettingsDrawer(false); }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel Edit
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 14px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: saving ? 'wait' : 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}
                >
                  <LuSave size={13} /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#e0f2fe',
                cursor: 'pointer',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px'
              }}
              title="Close modal"
            >
              <LuX size={20} />
            </button>
          </div>
        </div>

        {/* Modal Main Content Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <LuLayers size={36} style={{ animation: 'spin 2s linear infinite', margin: '0 auto 12px auto' }} />
              <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Loading template information...</p>
            </div>
          ) : mode === 'CREATE' ? (
            /* ========================================================
               CREATE NEW TEMPLATE FORM
               ======================================================== */
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', backgroundColor: '#f8fafc' }}>
              <form id="templateCreateForm" onSubmit={handleCreate}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Template Name */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                      Template Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={createForm.templateName}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, templateName: e.target.value }))}
                      placeholder="e.g. Prophecy Consulting Inc — Master Services Agreement (Supplier)"
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.9rem',
                        boxSizing: 'border-box'
                      }}
                      autoFocus
                    />
                  </div>

                  {/* Document Type & Side Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                        Document Type *
                      </label>
                      <select
                        value={createForm.documentTypeCode}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, documentTypeCode: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.88rem',
                          backgroundColor: '#ffffff'
                        }}
                      >
                        <option value="MSA">MSA — Master Services Agreement</option>
                        <option value="PO">PO — Purchase Order / Work Order</option>
                        <option value="SOW">SOW — Statement of Work</option>
                        <option value="NDA">NDA — Non-Disclosure Agreement</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                        Applicable Side *
                      </label>
                      <select
                        value={createForm.counterpartySide}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, counterpartySide: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.88rem',
                          backgroundColor: '#ffffff'
                        }}
                      >
                        <option value="SUPPLIER">Supplier Side (Vendors, Subcontractors, C2C)</option>
                        <option value="CLIENT">Client Side (End Clients, Prime Vendors)</option>
                        <option value="BOTH">Both Sides (Universal Template)</option>
                      </select>
                    </div>
                  </div>

                  {/* Governing Entity & Default Checkbox */}
                  <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div>
                      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '3px' }}>
                        Governing Entity
                      </span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 650, color: '#0f172a' }}>
                        {governingEntityName} (ID: #{governingEntityId || 4})
                      </span>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', color: '#1e293b' }}>
                      <input
                        type="checkbox"
                        checked={createForm.isDefault}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                        style={{ width: '16px', height: '16px', accentColor: '#0284c7' }}
                      />
                      <span style={{ fontWeight: 600 }}>Set as Default Template for {createForm.documentTypeCode}</span>
                      <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                        (Auto-selected by default when onboarding candidates & drafting agreements)
                      </span>
                    </label>
                  </div>

                  {/* Notes & Guidelines */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '5px' }}>
                      Template Instructions & Guidelines (Optional)
                    </label>
                    <textarea
                      rows={3}
                      value={createForm.notes}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="e.g. Standard Master Services Agreement for US subcontracting engagements. Net 60 payment terms with 3% Net 45 discount."
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.85rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Template File Attachment */}
                  <div style={{
                    border: '2px dashed #cbd5e1',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#ffffff',
                    textAlign: 'center'
                  }}>
                    <LuUpload size={28} color="#0284c7" style={{ margin: '0 auto 8px auto', display: 'block' }} />
                    <div style={{ fontSize: '0.9rem', fontWeight: 650, color: '#0f172a', marginBottom: '4px' }}>
                      Upload Template Document (.docx, .doc, .pdf)
                    </div>
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.78rem', color: '#64748b' }}>
                      Upload your standard legal Word contract template. In-system document preview will be generated automatically.
                    </p>

                    <input
                      type="file"
                      id="create-template-file-input"
                      accept=".docx,.doc,.pdf"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setCreateFile(e.target.files[0]);
                        }
                      }}
                      style={{ display: 'none' }}
                    />

                    <button
                      type="button"
                      onClick={() => document.getElementById('create-template-file-input')?.click()}
                      style={{
                        background: '#0284c7',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '7px 16px',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <LuFilePlus size={14} /> Select File from Computer
                    </button>

                    {createFile && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 14px',
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '20px',
                        marginTop: '12px',
                        fontSize: '0.82rem',
                        color: '#0369a1',
                        fontWeight: 600
                      }}>
                        <LuCheck size={15} />
                        <span>{createFile.name} ({formatFileSize(createFile.size)})</span>
                        <button
                          type="button"
                          onClick={() => setCreateFile(null)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 2px' }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          ) : mode === 'VIEW' ? (
            /* ========================================================
               VIEW MODE (FULL DOCUMENT VIEW OR DETAILS & VERSIONS)
               ======================================================== */
            viewTab === 'DOCUMENT' ? (
              /* TAB 1: FULL DOCUMENT VIEW (Rich A4 Paper Experience) */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#525659' }}>
                {/* Document View Toolbar */}
                <div style={{
                  padding: '8px 18px',
                  backgroundColor: '#111827',
                  borderBottom: '1px solid #1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: '#ffffff',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 650, color: '#f3f4f6', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <LuFileText size={15} color="#38bdf8" /> {previewData?.fileName || currentTmpl.OriginalFileName || currentTmpl.TemplateName}
                    </span>
                    <span style={{ fontSize: '0.72rem', backgroundColor: '#1f2937', color: '#0284c7', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                      {isDocxFile ? 'Word Template (.docx)' : (isPdfFile ? 'PDF Document' : 'Document Template')}
                    </span>
                    {isDocxFile && docPages.length > 0 && (
                      <span style={{ fontSize: '0.72rem', backgroundColor: '#374151', color: '#93c5fd', padding: '2px 8px', borderRadius: '4px', fontWeight: 650 }}>
                        {docPages.length} {docPages.length === 1 ? 'Page' : 'Pages'}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Zoom Controls for docx viewing */}
                    {isDocxFile && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', backgroundColor: '#1f2937', borderRadius: '6px', padding: '2px 4px' }}>
                        <button
                          type="button"
                          onClick={() => setZoomLevel(prev => Math.max(60, prev - 10))}
                          style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: '3px 6px', borderRadius: '4px' }}
                          title="Zoom Out"
                        >
                          <LuZoomOut size={13} />
                        </button>
                        <span style={{ fontSize: '0.74rem', color: '#d1d5db', minWidth: '38px', textAlign: 'center' }}>
                          {zoomLevel}%
                        </span>
                        <button
                          type="button"
                          onClick={() => setZoomLevel(prev => Math.min(150, prev + 10))}
                          style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: '3px 6px', borderRadius: '4px' }}
                          title="Zoom In"
                        >
                          <LuZoomIn size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setZoomLevel(100)}
                          style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '3px 4px' }}
                          title="Reset Zoom"
                        >
                          <LuRotateCcw size={11} />
                        </button>
                      </div>
                    )}

                    {/* Open in New Tab for PDF/File */}
                    {(previewData?.pdfUrl || previewData?.fileUrl) && (
                      <a
                        href={`${BASE_URL}${previewData.pdfUrl || previewData.fileUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          backgroundColor: '#1f2937',
                          border: '1px solid #4b5563',
                          borderRadius: '5px',
                          color: '#f3f4f6',
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                        title="Open document in full new window"
                      >
                        <LuExternalLink size={13} /> New Tab
                      </a>
                    )}

                    {/* Print / Save PDF */}
                    <button
                      type="button"
                      onClick={handlePrintDocument}
                      style={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #4b5563',
                        borderRadius: '5px',
                        color: '#f3f4f6',
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                      title="Print or Save as PDF"
                    >
                      <LuPrinter size={13} /> Print / Save
                    </button>

                    {/* Download File */}
                    <a
                      href={`${BASE_URL}/api/crm/commercial-documents/templates/${currentTmpl.CommercialTemplateId}/download`}
                      download
                      style={{
                        backgroundColor: '#0284c7',
                        border: 'none',
                        borderRadius: '5px',
                        color: '#ffffff',
                        padding: '4px 12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textDecoration: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      <LuDownload size={13} /> Download (.docx)
                    </a>
                  </div>
                </div>

                {/* Document Container */}
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: previewData?.pdfUrl ? '6px' : '32px 20px',
                  backgroundColor: '#f1f5f9',
                  boxSizing: 'border-box'
                }}>
                  {previewLoading ? (
                    <div style={{ padding: '60px', color: '#64748b', textAlign: 'center' }}>
                      <LuLayers size={36} style={{ animation: 'spin 2s linear infinite', margin: '0 auto 12px auto' }} />
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>Loading document...</p>
                    </div>
                  ) : isPdfFile && previewData?.pdfUrl ? (
                    /* 1. ORIGINAL PDF VIEWER (for PDF templates only) */
                    <div style={{
                      width: '100%',
                      maxWidth: '960px',
                      margin: '0 auto',
                      height: '100%',
                      minHeight: '820px',
                      backgroundColor: '#334155',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <iframe
                        src={`${BASE_URL}${previewData.pdfUrl}#toolbar=1&navpanes=0`}
                        title="Original Document Preview"
                        style={{
                          width: '100%',
                          height: '80vh',
                          minHeight: '820px',
                          border: 'none',
                          display: 'block'
                        }}
                      />
                    </div>
                  ) : previewData?.html ? (
                    /* 2. INSTANT WORD DOCUMENT PAPER VIEW (Rendered directly from .docx file) */
                    <>
                      <style>{`
                        .template-document-paper {
                          background-color: #ffffff !important;
                          box-sizing: border-box !important;
                          font-family: 'Cambria', Georgia, 'Times New Roman', serif !important;
                          color: #111827 !important;
                        }
                        .template-document-paper * {
                          box-sizing: border-box;
                        }
                        .template-document-paper .doc-top-bar {
                          width: 100%;
                          height: 3.5px;
                          background: #8E425F;
                          margin-bottom: 24px;
                          border-radius: 2px;
                        }
                        .template-document-paper .doc-title-heading {
                          text-align: center !important;
                          font-size: 15pt !important;
                          font-weight: 700 !important;
                          text-transform: uppercase !important;
                          letter-spacing: 0.8px !important;
                          color: #0f172a !important;
                          margin: 16px 0 24px 0 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper .doc-section-heading {
                          margin: 18px 0 10px 0 !important;
                          font-size: 11.5pt !important;
                          font-weight: 700 !important;
                          color: #0f172a !important;
                          letter-spacing: 0.5px !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper p {
                          margin: 0 0 13px 0 !important;
                          color: #111827 !important;
                          line-height: 1.65 !important;
                          text-align: justify !important;
                          text-justify: inter-word !important;
                          font-size: 11pt !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper ol {
                          padding-left: 28px !important;
                          margin: 10px 0 16px 0 !important;
                          color: #111827 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper ul {
                          padding-left: 28px !important;
                          margin: 8px 0 14px 0 !important;
                          color: #111827 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper li {
                          margin-bottom: 11px !important;
                          color: #111827 !important;
                          line-height: 1.65 !important;
                          text-align: justify !important;
                          text-justify: inter-word !important;
                          font-size: 11pt !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper li strong,
                        .template-document-paper li u,
                        .template-document-paper strong {
                          color: #0f172a !important;
                        }
                        .template-document-paper table {
                          width: 100% !important;
                          border-collapse: collapse !important;
                          margin: 20px 0 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper table td,
                        .template-document-paper table th {
                          border: 1px solid #94a3b8 !important;
                          padding: 10px 14px !important;
                          vertical-align: top !important;
                          font-size: 10.5pt !important;
                          color: #0f172a !important;
                          line-height: 1.5 !important;
                        }
                        .template-document-paper table tr td:first-child {
                          font-weight: 600 !important;
                          background-color: #f8fafc !important;
                          width: 32% !important;
                        }
                        .template-document-paper .doc-footer-banner {
                          margin-top: 50px !important;
                          margin-left: -56px !important;
                          margin-right: -56px !important;
                          margin-bottom: -48px !important;
                          width: calc(100% + 112px) !important;
                          border-radius: 0 0 6px 6px !important;
                          overflow: hidden !important;
                          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
                          display: block !important;
                        }
                        .template-document-paper ol.sub-clause-alpha,
                        .template-document-paper .sub-clause-alpha,
                        .template-document-paper .sub-clause-alpha li,
                        .template-document-paper ol[type="a"],
                        .template-document-paper ol[type="a"] li {
                          list-style-type: lower-alpha !important;
                        }
                        .template-document-paper .doc-footer-accent {
                          height: 4px;
                          background-color: #8E425F;
                          width: 100%;
                        }
                        .template-document-paper .doc-footer-body {
                          background-color: #01497C;
                          padding: 14px 24px;
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 16px;
                          color: #ffffff;
                        }
                        .template-document-paper .doc-footer-text {
                          text-align: right;
                          font-size: 11px;
                          line-height: 1.45;
                          color: #ffffff;
                          font-family: 'Cambria', Georgia, serif;
                        }
                        .template-document-paper .doc-footer-text a {
                          color: #ffffff !important;
                          text-decoration: underline !important;
                        }
                        @media print {
                          body * {
                            visibility: hidden;
                          }
                          .template-document-paper, .template-document-paper * {
                            visibility: visible;
                          }
                          .template-document-paper {
                            position: relative !important;
                            left: 0;
                            top: 0;
                            width: 100% !important;
                            max-width: 100% !important;
                            box-shadow: none !important;
                            border: none !important;
                            padding: 20px 40px !important;
                            page-break-after: always !important;
                            break-after: page !important;
                            page-break-inside: avoid !important;
                          }
                        }
                      `}</style>
                      {docPages.map((pageHtml, pageIdx) => (
                        <div key={pageIdx} style={{ width: '100%', marginBottom: pageIdx === docPages.length - 1 ? '40px' : '0' }}>
                          <div
                            ref={pageIdx === 0 ? printAreaRef : null}
                            className="template-document-paper"
                            style={{
                              backgroundColor: '#ffffff',
                              width: '100%',
                              maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                              margin: '0 auto',
                              padding: `${Math.round(48 * (zoomLevel / 100))}px ${Math.round(56 * (zoomLevel / 100))}px`,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              color: '#111827',
                              fontSize: `${14.5 * (zoomLevel / 100)}px`,
                              lineHeight: 1.65,
                              fontFamily: "'Cambria', Georgia, 'Times New Roman', serif",
                              minHeight: '750px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              boxSizing: 'border-box'
                            }}
                          >
                            <div>
                              {/* Word Template Header Accent Bar */}
                              <div className="doc-top-bar" />

                              {/* Page Header Bar with Page Number indicator */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.78rem',
                                color: '#64748b',
                                marginBottom: '18px',
                                fontFamily: "'Cambria', Georgia, serif"
                              }}>
                                <span style={{ fontWeight: 600, letterSpacing: '0.2px' }}>
                                  {currentTmpl?.DocumentTypeCode === 'PO' ? 'VENDOR WORK ORDER' : 'MASTER SERVICES AGREEMENT'}
                                </span>
                                <span style={{
                                  fontWeight: 700,
                                  backgroundColor: '#f1f5f9',
                                  color: '#475569',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  border: '1px solid #e2e8f0',
                                  fontSize: '0.74rem'
                                }}>
                                  Page {pageIdx + 1} of {docPages.length}
                                </span>
                              </div>

                              {/* Word Template Document Content for this page */}
                              <div dangerouslySetInnerHTML={{ __html: formatDocumentHtml(pageHtml) }} />
                            </div>

                            {/* Word Template Footer Banner for each page */}
                            <div
                              className="doc-footer-banner"
                              style={{
                                marginTop: '50px',
                                marginLeft: `-${Math.round(56 * (zoomLevel / 100))}px`,
                                marginRight: `-${Math.round(56 * (zoomLevel / 100))}px`,
                                marginBottom: `-${Math.round(48 * (zoomLevel / 100))}px`,
                                width: `calc(100% + ${Math.round(112 * (zoomLevel / 100))}px)`,
                                borderRadius: '0 0 6px 6px',
                                overflow: 'hidden',
                                display: 'block'
                              }}
                            >
                              <div className="doc-footer-accent" />
                              <div className="doc-footer-body">
                                <div className="doc-footer-logo">
                                  <img
                                    src="/prophecy_logo.png"
                                    alt="Prophecy Technologies"
                                    style={{ height: `${Math.round(34 * (zoomLevel / 100))}px`, objectFit: 'contain', display: 'block' }}
                                  />
                                </div>
                                <div className="doc-footer-text">
                                  <div style={{ fontWeight: 700, letterSpacing: '0.2px' }}>
                                    Prophecy Consulting Inc <span style={{ opacity: 0.7, fontWeight: 400 }}>|</span> <a href="https://www.prophecytechs.com" target="_blank" rel="noopener noreferrer">www.prophecytechs.com</a>
                                  </div>
                                  <div>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</div>
                                  <div>
                                    <a href="mailto:onboarding@prophecytechs.com">onboarding@prophecytechs.com</a> <span style={{ opacity: 0.7 }}>|</span> +1(551) 689-4006
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Visual Page Break Separator Between Pages */}
                          {pageIdx < docPages.length - 1 && (
                            <div style={{
                              maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                              margin: '24px auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '14px'
                            }}>
                              <div style={{ flex: 1, height: '1px', backgroundColor: '#cbd5e1' }} />
                              <span style={{
                                fontSize: '0.74rem',
                                fontWeight: 650,
                                color: '#475569',
                                backgroundColor: '#e2e8f0',
                                padding: '3px 12px',
                                borderRadius: '12px',
                                letterSpacing: '0.3px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                              }}>
                                Page {pageIdx + 1} End · Next: Page {pageIdx + 2}
                              </span>
                              <div style={{ flex: 1, height: '1px', backgroundColor: '#cbd5e1' }} />
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    /* Fallback: Rich Formatted Legal Template Contract Preview */
                    <div
                      className="template-document-paper"
                      style={{
                        backgroundColor: '#ffffff',
                        width: '100%',
                        maxWidth: `${Math.round(820 * (zoomLevel / 100))}px`,
                        margin: '0 auto 40px auto',
                        padding: `${Math.round(48 * (zoomLevel / 100))}px ${Math.round(56 * (zoomLevel / 100))}px`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        color: '#111827',
                        fontSize: `${14 * (zoomLevel / 100)}px`,
                        lineHeight: 1.65,
                        fontFamily: "'Cambria', Georgia, 'Times New Roman', serif",
                        minHeight: 'fit-content',
                        height: 'auto',
                        display: 'block',
                        boxSizing: 'border-box'
                      }}
                    >
                      {/* Word Template Header Accent Bar */}
                      <div className="doc-top-bar" />

                      <div style={{ textAlign: 'center', marginBottom: '24px', borderBottom: '2px solid #0284c7', paddingBottom: '14px' }}>
                        <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {currentTmpl.DocumentTypeCode === 'PO' ? 'PURCHASE ORDER & WORK ORDER AGREEMENT' : 'MASTER SERVICES AGREEMENT'}
                        </h2>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                          Governing Entity: <strong>{currentTmpl.GoverningEntityName || governingEntityName}</strong>
                        </div>
                      </div>

                      <p style={{ margin: '0 0 14px 0', textAlign: 'justify' }}>
                        THIS <strong>{currentTmpl.DocumentTypeCode === 'PO' ? 'PURCHASE ORDER' : 'MASTER SERVICES AGREEMENT'}</strong> (“Agreement”) is entered into by and between <strong>{currentTmpl.GoverningEntityName || governingEntityName}</strong> (“Client”) and the authorized counterparty entity (“Provider” or “Subcontractor”).
                      </p>

                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0284c7', marginTop: '16px', marginBottom: '6px' }}>
                        1. SERVICES AND SCOPE OF WORK
                      </div>
                      <p style={{ margin: '0 0 12px 0', textAlign: 'justify' }}>
                        Provider shall supply qualified technical personnel and perform commercial services in strict accordance with the requisitions, work orders, and candidate placements agreed upon under this Agreement.
                      </p>

                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0284c7', marginTop: '16px', marginBottom: '6px' }}>
                        2. PAYMENT TERMS & DISCOUNTS
                      </div>
                      <p style={{ margin: '0 0 12px 0', textAlign: 'justify' }}>
                        Invoices shall be processed based on approved timesheets. Standard terms are Net 60 days, with early payment options of 3% for Net 45 and 5% for Net 30 as specified in the applicable Statement of Work.
                      </p>

                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0284c7', marginTop: '16px', marginBottom: '6px' }}>
                        3. CONFIDENTIALITY & WORK PRODUCT
                      </div>
                      <p style={{ margin: '0 0 12px 0', textAlign: 'justify' }}>
                        All deliverables, inventions, code, and documents created in connection with the Services shall be deemed works-made-for-hire and the exclusive property of the Governing Entity.
                      </p>

                      <div style={{ marginTop: '36px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>For Governing Entity:</div>
                          <div style={{ marginTop: '24px', borderBottom: '1px solid #334155', width: '80%' }}></div>
                          <div style={{ marginTop: '6px', fontWeight: 650, fontSize: '0.82rem' }}>{currentTmpl.GoverningEntityName || governingEntityName}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>For Provider / Subcontractor:</div>
                          <div style={{ marginTop: '24px', borderBottom: '1px solid #334155', width: '80%' }}></div>
                          <div style={{ marginTop: '6px', fontWeight: 650, fontSize: '0.82rem' }}>Authorized Representative</div>
                        </div>
                      </div>

                      {/* Word Template Footer Banner */}
                      <div
                        className="doc-footer-banner"
                        style={{
                          marginTop: '50px',
                          marginLeft: `-${Math.round(56 * (zoomLevel / 100))}px`,
                          marginRight: `-${Math.round(56 * (zoomLevel / 100))}px`,
                          marginBottom: `-${Math.round(48 * (zoomLevel / 100))}px`,
                          width: `calc(100% + ${Math.round(112 * (zoomLevel / 100))}px)`,
                          borderRadius: '0 0 6px 6px',
                          overflow: 'hidden',
                          display: 'block'
                        }}
                      >
                        <div className="doc-footer-accent" />
                        <div className="doc-footer-body">
                          <div className="doc-footer-logo">
                            <img
                              src="/prophecy_logo.png"
                              alt="Prophecy Technologies"
                              style={{ height: `${Math.round(34 * (zoomLevel / 100))}px`, objectFit: 'contain', display: 'block' }}
                            />
                          </div>
                          <div className="doc-footer-text">
                            <div style={{ fontWeight: 700, letterSpacing: '0.2px' }}>
                              Prophecy Consulting Inc <span style={{ opacity: 0.7, fontWeight: 400 }}>|</span> <a href="https://www.prophecytechs.com" target="_blank" rel="noopener noreferrer">www.prophecytechs.com</a>
                            </div>
                            <div>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</div>
                            <div>
                              <a href="mailto:onboarding@prophecytechs.com">onboarding@prophecytechs.com</a> <span style={{ opacity: 0.7 }}>|</span> +1(551) 689-4006
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* TAB 2: DETAILS & VERSIONS */
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', backgroundColor: '#f8fafc' }}>
                {/* Top Banner / Tags */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  marginBottom: '18px'
                }}>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    backgroundColor: currentTmpl.DocumentTypeCode === 'MSA' ? '#ede9fe' : '#e0f2fe',
                    color: currentTmpl.DocumentTypeCode === 'MSA' ? '#6b21a8' : '#0369a1'
                  }}>
                    {currentTmpl.DocumentTypeCode}
                  </span>

                  <span style={{
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '0.74rem',
                    fontWeight: 600,
                    backgroundColor: '#f1f5f9',
                    color: '#475569'
                  }}>
                    Version v{currentTmpl.CurrentVersionNo || '1.0'}
                  </span>

                  {currentTmpl.IsDefault ? (
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '20px',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      backgroundColor: '#dcfce7',
                      color: '#0369a1',
                      border: '1px solid #bbf7d0'
                    }}>
                      Default Template ★
                    </span>
                  ) : (
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '20px',
                      fontSize: '0.74rem',
                      fontWeight: 500,
                      backgroundColor: '#f8fafc',
                      color: '#64748b',
                      border: '1px solid #e2e8f0'
                    }}>
                      Standard Template
                    </span>
                  )}
                </div>

                {/* Info Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '14px',
                  marginBottom: '20px',
                  backgroundColor: '#ffffff',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Template Name
                    </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>
                      {currentTmpl.TemplateName}
                    </span>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Governing Entity
                    </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#0f172a' }}>
                      {currentTmpl.GoverningEntityName || governingEntityName}
                    </span>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Applicable Side
                    </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#0f172a' }}>
                      {currentTmpl.CounterpartySide || 'Both (Supplier & Client)'}
                    </span>
                  </div>

                  <div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>
                      Document Type
                    </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 500, color: '#0f172a' }}>
                      {currentTmpl.DocumentTypeName || currentTmpl.DocumentTypeCode}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                {currentTmpl.Notes && (
                  <div style={{ marginBottom: '20px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Template Notes & Instructions
                    </span>
                    <div style={{ fontSize: '0.84rem', color: '#334155', backgroundColor: '#fff', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: '6px' }}>
                      {currentTmpl.Notes}
                    </div>
                  </div>
                )}

                {/* Current Active File Card */}
                <div style={{ marginBottom: '22px' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Attached Template File
                  </span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: '#f0fdfa',
                    border: '1px solid #e0f2fe',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '6px',
                        backgroundColor: '#e0f2fe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0284c7'
                      }}>
                        <LuFileText size={18} />
                      </div>
                      <div>
                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0284c7', display: 'block' }}>
                          {currentTmpl.OriginalFileName || `${currentTmpl.TemplateName}.docx`}
                        </span>
                        <span style={{ fontSize: '0.74rem', color: '#38bdf8' }}>
                          {formatFileSize(currentTmpl.FileSizeBytes)} • Word Template Document
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setViewTab('DOCUMENT')}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '6px 14px',
                          borderRadius: '6px',
                          backgroundColor: '#ffffff',
                          color: '#0284c7',
                          border: '1px solid #0284c7',
                          fontSize: '0.78rem',
                          fontWeight: 650,
                          cursor: 'pointer'
                        }}
                      >
                        <LuEye size={13} /> View Full Document
                      </button>

                      <a
                        href={`${BASE_URL}/api/crm/commercial-documents/templates/${currentTmpl.CommercialTemplateId}/download`}
                        download
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '6px 14px',
                          borderRadius: '6px',
                          backgroundColor: '#0284c7',
                          color: '#ffffff',
                          textDecoration: 'none',
                          fontSize: '0.78rem',
                          fontWeight: 600
                        }}
                      >
                        <LuDownload size={13} /> Download File
                      </a>
                    </div>
                  </div>
                </div>

                {/* Version History Table */}
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', textTransform: 'uppercase', marginBottom: '8px' }}>
                    Version History ({currentTmpl.versions?.length || 1})
                  </span>
                  <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b' }}>
                          <th style={{ padding: '8px 12px' }}>Version</th>
                          <th style={{ padding: '8px 12px' }}>File Name</th>
                          <th style={{ padding: '8px 12px' }}>Note / Change</th>
                          <th style={{ padding: '8px 12px' }}>Uploaded At</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(currentTmpl.versions || []).map((v) => (
                          <tr key={v.CommercialTemplateVersionId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, color: '#0f172a' }}>
                              v{v.VersionNo}.0 {v.VersionNo === currentTmpl.CurrentVersionNo ? '(Active)' : ''}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#334155' }}>
                              {v.OriginalFileName || currentTmpl.OriginalFileName || 'Template Document'}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#64748b' }}>
                              {v.ChangeNote || 'Standard template baseline'}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#64748b' }}>
                              {v.CreatedAtUtc ? new Date(v.CreatedAtUtc).toLocaleDateString() : '—'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              <a
                                href={`${BASE_URL}/api/crm/commercial-documents/templates/${currentTmpl.CommercialTemplateId}/download`}
                                download
                                style={{ color: '#0284c7', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                title="Download version"
                              >
                                <LuDownload size={12} /> Get
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          ) : (
            /* ========================================================
               EDIT MODE (FULL INTERACTIVE DOCX EDITABLE VIEW)
               ======================================================== */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#525659' }}>
              {/* 1. Quick Collapsible Settings Drawer for Template Metadata */}
              {showSettingsDrawer && (
                <div style={{
                  padding: '14px 22px',
                  backgroundColor: '#f8fafc',
                  borderBottom: '2px solid #0284c7',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  flexShrink: 0
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                        Template Name *
                      </label>
                      <input
                        type="text"
                        value={formData.templateName}
                        onChange={(e) => setFormData(prev => ({ ...prev, templateName: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Template Name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                        Applicable Side
                      </label>
                      <select
                        value={formData.counterpartySide}
                        onChange={(e) => setFormData(prev => ({ ...prev, counterpartySide: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          backgroundColor: '#ffffff'
                        }}
                      >
                        <option value="BOTH">Both (Supplier & Client)</option>
                        <option value="SUPPLIER">Supplier Side Only</option>
                        <option value="CLIENT">Client Side Only</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>
                        Version Change Note (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.changeNote}
                        onChange={(e) => setFormData(prev => ({ ...prev, changeNote: e.target.value }))}
                        placeholder="e.g. Updated indemnity & payment terms"
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.82rem', color: '#1e293b' }}>
                      <input
                        type="checkbox"
                        checked={formData.isDefault}
                        onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                        style={{ accentColor: '#0284c7', width: '15px', height: '15px' }}
                      />
                      <span style={{ fontWeight: 600 }}>Set as Default Template for {currentTmpl.DocumentTypeCode || 'MSA'}</span>
                    </label>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '0.76rem', color: '#64748b' }}>Replace with new file (optional):</span>
                      <input
                        type="file"
                        accept=".docx,.doc,.pdf"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setNewFile(e.target.files[0]);
                          }
                        }}
                        style={{ fontSize: '0.76rem' }}
                      />
                      {newFile && (
                        <span style={{ fontSize: '0.76rem', color: '#0369a1', fontWeight: 650 }}>
                          ✓ {newFile.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 2. Word-like Editing Toolbar */}
              <div style={{
                padding: '7px 16px',
                backgroundColor: '#111827',
                borderBottom: '1px solid #1f2937',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#ffffff',
                flexShrink: 0,
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {/* Formatting Buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('bold'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontWeight: 700, fontSize: '0.82rem' }}
                    title="Bold (Ctrl+B)"
                  >
                    <LuBold size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('italic'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontStyle: 'italic', fontSize: '0.82rem' }}
                    title="Italic (Ctrl+I)"
                  >
                    <LuItalic size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('underline'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', textDecoration: 'underline', fontSize: '0.82rem' }}
                    title="Underline (Ctrl+U)"
                  >
                    <LuUnderline size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('strikeThrough'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.82rem' }}
                    title="Strikethrough"
                  >
                    <LuStrikethrough size={13} />
                  </button>

                  <div style={{ width: '1px', height: '18px', backgroundColor: '#374151', margin: '0 4px' }} />

                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('justifyLeft'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer' }}
                    title="Align Left"
                  >
                    <LuAlignLeft size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('justifyCenter'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer' }}
                    title="Align Center"
                  >
                    <LuAlignCenter size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('justifyRight'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer' }}
                    title="Align Right"
                  >
                    <LuAlignRight size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('justifyFull'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer' }}
                    title="Justify"
                  >
                    <LuAlignJustify size={13} />
                  </button>

                  <div style={{ width: '1px', height: '18px', backgroundColor: '#374151', margin: '0 4px' }} />

                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('insertUnorderedList'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer' }}
                    title="Bullet List"
                  >
                    <LuList size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('insertOrderedList'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#ffffff', padding: '5px 8px', cursor: 'pointer' }}
                    title="Numbered List"
                  >
                    <LuListOrdered size={13} />
                  </button>

                  <div style={{ width: '1px', height: '18px', backgroundColor: '#374151', margin: '0 4px' }} />

                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('undo'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#9ca3af', padding: '5px 8px', cursor: 'pointer' }}
                    title="Undo (Ctrl+Z)"
                  >
                    <LuUndo size={13} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); executeFormat('redo'); }}
                    style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#9ca3af', padding: '5px 8px', cursor: 'pointer' }}
                    title="Redo (Ctrl+Y)"
                  >
                    <LuRedo size={13} />
                  </button>
                </div>

                {/* Right controls: Page badge, Zoom & Settings */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.72rem', backgroundColor: '#1e3a8a', color: '#93c5fd', padding: '3px 8px', borderRadius: '4px', fontWeight: 700, border: '1px solid #2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <LuPencil size={11} /> All Content Editable ({docPages.length} {docPages.length === 1 ? 'Page' : 'Pages'})
                  </span>

                  {/* Zoom Controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px', backgroundColor: '#1f2937', borderRadius: '6px', padding: '2px 4px' }}>
                    <button
                      type="button"
                      onClick={() => setZoomLevel(prev => Math.max(60, prev - 10))}
                      style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: '3px 6px', borderRadius: '4px' }}
                      title="Zoom Out"
                    >
                      <LuZoomOut size={13} />
                    </button>
                    <span style={{ fontSize: '0.74rem', color: '#d1d5db', minWidth: '38px', textAlign: 'center' }}>
                      {zoomLevel}%
                    </span>
                    <button
                      type="button"
                      onClick={() => setZoomLevel(prev => Math.min(150, prev + 10))}
                      style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: '3px 6px', borderRadius: '4px' }}
                      title="Zoom In"
                    >
                      <LuZoomIn size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setZoomLevel(100)}
                      style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '3px 4px' }}
                      title="Reset Zoom"
                    >
                      <LuRotateCcw size={11} />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowSettingsDrawer(prev => !prev)}
                    style={{
                      background: showSettingsDrawer ? '#0284c7' : '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '5px',
                      color: '#ffffff',
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 650,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Template Settings"
                  >
                    <LuSettings size={13} /> Settings
                  </button>
                </div>
              </div>

              {/* 3. Hint banner */}
              <div style={{
                padding: '6px 16px',
                backgroundColor: '#eff6ff',
                borderBottom: '1px solid #bfdbfe',
                color: '#1e40af',
                fontSize: '0.76rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <LuSparkles size={14} color="#2563eb" />
                  <strong>Interactive Word Document Editor:</strong> Click directly into any clause, heading, table, or paragraph below to type and edit. All changes are saved directly into the template.
                </span>
                <span style={{ color: '#2563eb', fontWeight: 600 }}>Ctrl+B (Bold) • Ctrl+I (Italic) • Ctrl+U (Underline)</span>
              </div>

              {/* 4. Full Multi-page Editable Document Canvas */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '32px 20px',
                backgroundColor: '#f1f5f9',
                boxSizing: 'border-box'
              }}>
                <style>{`
                  .template-editable-paper:hover {
                    border-color: #0284c7 !important;
                  }
                  .template-editable-page-content:hover {
                    background-color: rgba(240, 253, 250, 0.3);
                    border-radius: 4px;
                  }
                  .template-editable-page-content:focus {
                    outline: 1.5px dashed #0284c7 !important;
                    outline-offset: 4px;
                    background-color: transparent !important;
                  }
                  .template-editable-page-content table td:focus,
                  .template-editable-page-content table th:focus {
                    outline: 2px solid #0284c7 !important;
                    background-color: #eff6ff !important;
                  }
                  .template-editable-page-content .doc-title-heading:focus,
                  .template-editable-page-content .doc-section-heading:focus {
                    outline: 1px dashed #0284c7 !important;
                  }
                `}</style>
                {previewLoading ? (
                  <div style={{ padding: '60px', color: '#64748b', textAlign: 'center' }}>
                    <LuLayers size={36} style={{ animation: 'spin 2s linear infinite', margin: '0 auto 12px auto' }} />
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>Loading document content...</p>
                  </div>
                ) : (
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {docPages.map((pageHtml, pageIdx) => (
                      <div key={pageIdx} style={{ width: '100%', marginBottom: pageIdx === docPages.length - 1 ? '40px' : '0' }}>
                        <div
                          className="template-document-paper template-editable-paper"
                          style={{
                            backgroundColor: '#ffffff',
                            width: '100%',
                            maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                            margin: '0 auto',
                            padding: `${Math.round(48 * (zoomLevel / 100))}px ${Math.round(56 * (zoomLevel / 100))}px`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
                            border: '1.5px solid #cbd5e1',
                            borderRadius: '6px',
                            color: '#111827',
                            fontSize: `${14.5 * (zoomLevel / 100)}px`,
                            lineHeight: 1.65,
                            fontFamily: "'Cambria', Georgia, 'Times New Roman', serif",
                            minHeight: '750px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            boxSizing: 'border-box',
                            transition: 'border-color 0.2s ease'
                          }}
                        >
                          <div>
                            {/* Word Template Header Accent Bar */}
                            <div
                              className="doc-top-bar"
                              style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: '#8E425F',
                                marginBottom: '24px',
                                borderRadius: '2px',
                                display: 'block'
                              }}
                            />

                            {/* Page Header Bar with Page Number indicator */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: '0.78rem',
                              color: '#64748b',
                              marginBottom: '18px',
                              fontFamily: "'Cambria', Georgia, serif",
                              userSelect: 'none'
                            }}>
                              <span style={{ fontWeight: 600, letterSpacing: '0.2px' }}>
                                {currentTmpl?.DocumentTypeCode === 'PO' ? 'VENDOR WORK ORDER' : 'MASTER SERVICES AGREEMENT'}
                              </span>
                              <span style={{
                                fontWeight: 700,
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.74rem'
                              }}>
                                Page {pageIdx + 1} of {docPages.length} • Editable
                              </span>
                            </div>

                            {/* Word Template Document Content for this page - FULLY EDITABLE */}
                            <div
                              ref={(el) => { pageDivRefs.current[pageIdx] = el; }}
                              contentEditable={true}
                              suppressContentEditableWarning={true}
                              className="template-editable-page-content"
                              dangerouslySetInnerHTML={{ __html: formatDocumentHtml(pageHtml) }}
                              style={{
                                outline: 'none',
                                minHeight: '350px',
                                cursor: 'text'
                              }}
                            />
                          </div>

                          {/* Word Template Footer Banner for each page */}
                          <div
                            className="doc-footer-banner"
                            style={{
                              marginTop: '50px',
                              marginLeft: `-${Math.round(56 * (zoomLevel / 100))}px`,
                              marginRight: `-${Math.round(56 * (zoomLevel / 100))}px`,
                              marginBottom: `-${Math.round(48 * (zoomLevel / 100))}px`,
                              width: `calc(100% + ${Math.round(112 * (zoomLevel / 100))}px)`,
                              borderRadius: '0 0 6px 6px',
                              overflow: 'hidden',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                              display: 'block'
                            }}
                          >
                            <div
                              className="doc-footer-accent"
                              style={{
                                height: '4px',
                                backgroundColor: '#8E425F',
                                width: '100%',
                                display: 'block'
                              }}
                            />
                            <div
                              className="doc-footer-body"
                              style={{
                                backgroundColor: '#01497C',
                                padding: '14px 24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px',
                                color: '#ffffff'
                              }}
                            >
                              <div className="doc-footer-logo" style={{ flexShrink: 0 }}>
                                <img
                                  src="/prophecy_logo.png"
                                  alt="Prophecy Technologies"
                                  style={{ height: `${Math.round(34 * (zoomLevel / 100))}px`, objectFit: 'contain', display: 'block' }}
                                />
                              </div>
                              <div
                                className="doc-footer-text"
                                style={{
                                  textAlign: 'right',
                                  fontSize: '11px',
                                  lineHeight: '1.45',
                                  color: '#ffffff',
                                  fontFamily: "'Cambria', Georgia, serif"
                                }}
                              >
                                <div style={{ fontWeight: 700, letterSpacing: '0.2px', color: '#ffffff' }}>
                                  Prophecy Consulting Inc <span style={{ opacity: 0.7, fontWeight: 400 }}>|</span> <a href="https://www.prophecytechs.com" target="_blank" rel="noopener noreferrer" style={{ color: '#ffffff', textDecoration: 'underline' }}>www.prophecytechs.com</a>
                                </div>
                                <div style={{ color: '#ffffff' }}>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</div>
                                <div style={{ color: '#ffffff' }}>
                                  <a href="mailto:onboarding@prophecytechs.com" style={{ color: '#ffffff', textDecoration: 'underline' }}>onboarding@prophecytechs.com</a> <span style={{ opacity: 0.7 }}>|</span> +1(551) 689-4006
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Visual Page Break Separator Between Pages */}
                        {pageIdx < docPages.length - 1 && (
                          <div style={{
                            maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                            margin: '24px auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '14px'
                          }}>
                            <div style={{ flex: 1, height: '1px', backgroundColor: '#cbd5e1' }} />
                            <span style={{
                              fontSize: '0.74rem',
                              fontWeight: 650,
                              color: '#475569',
                              backgroundColor: '#e2e8f0',
                              padding: '3px 12px',
                              borderRadius: '12px',
                              userSelect: 'none'
                            }}>
                              Page {pageIdx + 1} of {docPages.length}
                            </span>
                            <div style={{ flex: 1, height: '1px', backgroundColor: '#cbd5e1' }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f8fafc',
          flexShrink: 0
        }}>
          {mode === 'CREATE' ? (
            <>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '7px 16px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                form="templateCreateForm"
                disabled={saving}
                style={{
                  padding: '8px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  fontSize: '0.85rem',
                  fontWeight: 650,
                  cursor: saving ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: saving ? 0.7 : 1
                }}
              >
                <LuFilePlus size={15} />
                <span>{saving ? 'Creating...' : 'Create Template'}</span>
              </button>
            </>
          ) : mode === 'VIEW' ? (
            <>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '7px 16px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setMode('EDIT')}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '6px',
                    border: '1px solid #c7d2fe',
                    backgroundColor: '#eef2ff',
                    color: '#4338ca',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <LuPencil size={14} />
                  <span>Edit Template</span>
                </button>

                {onUseTemplate && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onUseTemplate(currentTmpl);
                    }}
                    style={{
                      padding: '7px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#0284c7',
                      color: '#ffffff',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <LuFilePlus size={14} />
                    <span>Use Template</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => { setMode('VIEW'); setNewFile(null); setShowSettingsDrawer(false); }}
                style={{
                  padding: '7px 16px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#334155',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Back to View
              </button>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setShowSettingsDrawer(prev => !prev)}
                  style={{
                    padding: '7px 14px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#475569',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <LuSettings size={13} />
                  <span>{showSettingsDrawer ? 'Hide Settings' : 'Template Settings'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '7px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    fontSize: '0.85rem',
                    fontWeight: 650,
                    cursor: saving ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: saving ? 0.7 : 1,
                    boxShadow: '0 2px 4px rgba(2, 132, 199, 0.25)'
                  }}
                >
                  <LuSave size={14} />
                  <span>{saving ? 'Saving Changes...' : 'Save Changes'}</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
